<template>
    <filter-wrapper :filterName='settings.name' :expand='expanded'>
            <div class='filter-holder'>
                <q-range
                    v-model="rangeValue"
                    :min="minRange"
                    :max="maxRange"
                    :step="step"
                    label-always
                    markers
                    :left-label-value="(itemsAscending.filter(item => item.value == rangeValue.min)[0] || {}).label"
                    :right-label-value="(itemsDescending.filter(item => item.value == rangeValue.max)[0] || {}).label"
                    class='range-slider'
                    @change="updateValue"
                />

                <q-select
                    dense
                    filled
                    label="Minimum"
                    :options="itemsAscending"
                    :use-input='settings.searchable'
                    @filter="filter"
                    :modelValue="rangeValue.min"
                    @update:modelValue="v => updateModelValue(v, 'min')"
                    map-options
                >   
                    <template v-slot:option="scope">
                        <q-item v-bind="scope.itemProps">
                            <q-item-section>
                                <q-item-label>{{ scope.opt.label }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>
                
                <q-select
                    dense
                    filled
                    label="Maximum"
                    :options="itemsAscending"
                    :use-input='settings.searchable'
                    @filter="filter"
                    :modelValue="rangeValue.max"
                    @update:modelValue="v => updateModelValue(v, 'max')"
                    map-options
                    class="q-pt-sm"
                >   
                    <template v-slot:option="scope">
                        <q-item v-bind="scope.itemProps">
                            <q-item-section>
                                <q-item-label>{{ scope.opt.label }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>

                <div v-if='options.length > 0' class="q-pt-md" >
                    <q-option-group
                        v-model="unitOfMeasure"
                        v-if='options.length > 0'
                        :options="options"
                        type="radio"
                        @update:model-value='v => updateConversion(v)'
                    />
                </div>
            </div>
    </filter-wrapper>
</template>

<script setup>
    import { ref, onMounted, defineProps, defineEmits, watch, computed } from 'vue'
    import FilterWrapper from './FilterWrapper.vue'

    const props = defineProps({
        settings: {
            type: Object,
            default: () => ({})
        },
        modelValue: {
            type: [String, Array, Object]
        },
        expanded: {
            type: Boolean,
            default: false
        }
    })
    const emit = defineEmits(['update:modelValue'])
    const temperatureConversions = [
        { label: 'Fahrenheit', value: 'fahrenheit' },
        { label: 'Celsius', value: 'celsius' },
    ]
    const measurementConversions = [
        { label: 'Imperial', value: 'imperial' },
        { label: 'Metric', value: 'metric' },
    ]

    const isFloat = ref(false)
    const step = ref(1)
    let converter = ref(0)
    let options = []
    let unitOfMeasure = ref('')
    let minRange = ref(0)
    let maxRange = ref(100)
    let rangeValue = ref({min: -999, max: 999})
    
    let minFahrenheit = ref(0)
    let maxFahrenheit = ref(0)
    let minCelsius = ref(0)
    let maxCelsius = ref(0)
    let minImperial = ref(0)
    let maxImperial = ref(0)
    let minMetric = ref(0)
    let maxMetric = ref(0)

    const itemsAscending = computed(() => {
        return props.settings.items.map(item => item).sort((a, b) => parseFloat(a.value) - parseFloat(b.value))
    })

    const itemsDescending = computed(() => {
        return props.settings.items.map(item => item).sort((a, b) => parseFloat(b.value) - parseFloat(a.value))
    })

    onMounted(() => {
        const { settings } = props
        determineValueTypes()

        step.value = isFloat.value ? 0.001 : 1

        converter.value = settings.conversionType
        
        if (converter.value == 1) {
            options = temperatureConversions
            unitOfMeasure.value = options[0].value
            
            //todo: get these values from the API
            minFahrenheit.value = 32
            maxFahrenheit.value = 212
            minCelsius.value = 0
            maxCelsius.value = 100
        } else if (converter.value == 2) {
            options = measurementConversions
            unitOfMeasure.value = options[0].value

            //todo: get these values from the API
            minImperial.value = 0.25
            maxImperial.value = 24.00
            minMetric.value = 1
            maxMetric.value = 60
        }
        setMinMaxValues()
        if (typeof props.modelValue == 'string') {
            const values = props.modelValue.value.replace('[', '').replace(']', '').split(',')
            setRangeValues(values)
        }
        else if (typeof props.modelValue == 'object' && props.modelValue?.value) {
            const values = props.modelValue.value.replace('[', '').replace(']', '').split(',') 
            setRangeValues(values)
        }
        else {
            rangeValue.value.min = minRange.value
            rangeValue.value.max = maxRange.value
        }
    })

    const setRangeValues = (valueArray) => {
        rangeValue.value.min = isFloat.value ? parseFloat(valueArray[0]) : parseInt(valueArray[0])
        rangeValue.value.max = isFloat.value ? parseFloat(valueArray[1]) : parseInt(valueArray[1])
    }

    watch(() => props.modelValue, (newValue) => {
        if (!newValue) {
            rangeValue.value.min = minRange.value;
            rangeValue.value.max = maxRange.value;
        }
    })

    const updateConversion = (value) => {
        unitOfMeasure.value = value
        setMinMaxValues()
    }

    const updateModelValue = (option, rangeProp) => {
        rangeValue.value[rangeProp] = option.value
        
        updateValue()
    }

    const updateValue = () => {
        if (rangeValue.value.min == minRange.value && rangeValue.value.max == maxRange.value) {
            emit('update:modelValue', '')
            return
        }
        const label = `${rangeValue.value.min} - ${rangeValue.value.max}`;
        const value = `[${rangeValue.value.min},${rangeValue.value.max}]`
        emit('update:modelValue', { label, value })
    }
    const setMinMaxValues = () => {
        const min = itemsAscending.value[0].value
        const max = itemsAscending.value[props.settings.items.length-1].value

        minRange.value = isFloat.value ? parseFloat(min) : parseInt(min)
        maxRange.value = isFloat.value ? parseFloat(max) : parseInt(max)

        // Leaving these here in case we need them in the future
        // if (converter.value == 1 && unitOfMeasure.value == 'fahrenheit') {
        //     minRange.value = minFahrenheit.value
        //     maxRange.value = maxFahrenheit.value
        // }
        // if (converter.value == 1 && unitOfMeasure.value == 'celsius') {
        //     minRange.value = minCelsius.value
        //     maxRange.value = maxCelsius.value
        // }
        // if (converter.value == 2 && unitOfMeasure.value == 'imperial') {
        //     minRange.value = minImperial.value
        //     maxRange.value = maxImperial.value
        // }
        // if (converter.value == 2 && unitOfMeasure.value == 'metric') {
        //     minRange.value = minMetric.value
        //     maxRange.value = maxMetric.value
        // }
    }

    const determineValueTypes = () => {

        isFloat.value = props.settings.items.some(item => {
            const int = parseInt(item.value)
            const float = parseFloat(item.value)
            if (int.toString().length < float.toString().length) return true
            return false
        })
        props.settings.items.forEach(item => {
            const value = isFloat.value ? parseFloat(item.value) : parseInt(item.value)
            item.value = value
        })
    }

</script>

<style lang='scss' scoped>
    // this is a hack to get the range slider to display correctly in both local and production 
    .range-slider,
    div#searchPage.searchPageReset .range-slider{
        padding: 25px 10px 0px;
    }
    .filter-holder {
        padding: 0px 12px 10px;
    }
</style>